/**
 * The Work view: everything a Claude session does, as the desktop app shows it. The session
 * chat on the left (each turn with its tool calls, cards and reply), the live work on the
 * right (the selected or latest tool in full, files, the plan, the checklist, usage).
 * Self contained: it knows only the event stream and three callbacks, so it can be lifted
 * out of Agora into another interface unchanged.
 */
import type { OpenedFile, PermissionMode, PromptAnswer, ToolOutput, Usage, WorkCommand, WorkEvent } from "@agora/agent-stream";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { PromptCard } from "./cards";
import { CodeView, DiffView, FileView } from "./code";
import { Markdown } from "./markdown";
import { emptyState, openPrompts, reduce, type Item, type ToolItem, type Turn, type WorkState } from "./state";
import "./workview.css";

export type ViewMode = "hidden" | "auto" | "dig";

export interface WorkViewProps {
  events: WorkEvent[];
  send: (cmd: WorkCommand) => Promise<void>;
  openFile: (path: string) => Promise<OpenedFile>;
  layout: "drawer" | "full";
  title: string;
  /** The person may drive the session: answer cards, write, stop, change the mode. */
  canDrive: boolean;
  /** Whether the session is running now (it closes when idle and resumes on the next message). */
  live?: boolean;
  viewMode?: ViewMode;
  onViewMode?: (m: ViewMode) => void;
  onClose?: () => void;
  /** Extra controls for the header (an "open full page" link, for example). */
  actions?: ReactNode;
}

/** Reduces as events arrive; rebuilds only when the list is replaced. */
export function useWorkState(events: WorkEvent[]): WorkState {
  const ref = useRef<{ state: WorkState; list: WorkEvent[]; n: number }>({ state: emptyState(), list: [], n: 0 });
  return useMemo(() => {
    const r = ref.current;
    const appended = r.list === events || (events.length >= r.n && r.n > 0 && events[r.n - 1] === r.list[r.n - 1]);
    let state = appended ? r.state : emptyState();
    for (let i = appended ? r.n : 0; i < events.length; i++) state = reduce(state, events[i]!);
    ref.current = { state, list: events, n: events.length };
    return state;
  }, [events]);
}

const fmt = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
const money = (n: number) => (n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`);
const secs = (ms: number) => (ms >= 60_000 ? `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s` : `${(ms / 1000).toFixed(1)} s`);
const STATE_WORD: Record<string, string> = { starting: "Starting", working: "Working", waiting: "Waiting for you", idle: "Ready", compacting: "Making room in its memory", closed: "Resting", error: "Stopped" };
const MODE_WORD: Record<string, string> = { default: "Ask before edits outside the rules", plan: "Plan first", acceptEdits: "Accept edits", auto: "Auto: a safety check decides, risky steps still ask" };

type Tab = "chat" | "live" | "files" | "plan" | "checklist" | "usage";

export function WorkView(p: WorkViewProps) {
  const s = useWorkState(p.events);
  const [tab, setTab] = useState<Tab>(p.layout === "drawer" ? "chat" : "live");
  const [selected, setSelected] = useState<string | null>(null);
  const [file, setFile] = useState<{ path: string; loading: boolean; error?: string; data?: OpenedFile } | null>(null);
  const waiting = openPrompts(s);
  const tools = Object.values(s.tools);
  const latest = tools.reduce<ToolItem | null>((a, t) => (!a || t.seq > a.seq ? t : a), null);
  const shown = (selected ? s.tools[selected] : null) ?? latest;

  const open = async (path: string) => {
    setFile({ path, loading: true });
    setTab("files");
    try {
      setFile({ path, loading: false, data: await p.openFile(path) });
    } catch (err) {
      setFile({ path, loading: false, error: err instanceof Error ? err.message : "The file could not be opened." });
    }
  };
  const select = (id: string) => {
    setSelected(id);
    setTab("live");
  };
  const answer = (promptId: string) => (a: PromptAnswer) => p.send({ type: "prompt.answer", promptId, answer: a });

  const tabs: { id: Tab; label: string; badge?: number }[] = [
    ...(p.layout === "drawer" ? [{ id: "chat" as Tab, label: "Chat", badge: waiting.length || undefined }] : []),
    { id: "live", label: "Live" },
    { id: "files", label: "Files", badge: s.files.length || undefined },
    { id: "plan", label: "Plan" },
    { id: "checklist", label: "Checklist", badge: s.todos.filter((t) => t.status !== "completed").length || undefined },
    { id: "usage", label: "Usage" },
  ];

  const chat = <Chat s={s} canDrive={p.canDrive} onSelect={select} onOpen={open} answer={answer} selected={shown?.id ?? null} send={p.send} />;
  const right = (
    <div className="wv-pane">
      {tab === "live" && (shown ? <ToolDetail item={shown} cwd={s.session?.cwd ?? ""} onOpen={open} following={!selected} onFollow={() => setSelected(null)} /> : <p className="wv-muted wv-pad">Nothing has run yet. Tool calls show here as they happen.</p>)}
      {tab === "files" && <Files s={s} file={file} onOpen={open} />}
      {tab === "plan" && <PlanPane s={s} />}
      {tab === "checklist" && <Checklist s={s} />}
      {tab === "usage" && <UsagePane s={s} />}
      {tab === "chat" && chat}
    </div>
  );

  return (
    <div className={`wv wv-${p.layout}`}>
      <Header s={s} p={p} />
      {p.layout === "full" ? (
        <div className="wv-split">
          <div className="wv-left">{chat}</div>
          <div className="wv-right">
            <Tabs tabs={tabs} tab={tab} setTab={setTab} />
            {right}
          </div>
        </div>
      ) : (
        <div className="wv-single">
          <Tabs tabs={tabs} tab={tab} setTab={setTab} />
          {right}
        </div>
      )}
    </div>
  );
}

function Tabs({ tabs, tab, setTab }: { tabs: { id: Tab; label: string; badge?: number }[]; tab: Tab; setTab: (t: Tab) => void }) {
  return (
    <div className="wv-tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? "on" : ""} onClick={() => setTab(t.id)}>
          {t.label}
          {t.badge ? <span className="wv-badge">{t.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

function Header({ s, p }: { s: WorkState; p: WorkViewProps }) {
  const auth = s.session?.auth;
  const working = s.status.state === "working" || s.status.state === "waiting" || s.status.state === "compacting";
  const [busy, setBusy] = useState(false);
  const run = async (cmd: WorkCommand) => {
    setBusy(true);
    try {
      await p.send(cmd);
    } finally {
      setBusy(false);
    }
  };
  const state = p.live === false && !working ? "closed" : s.status.state;
  return (
    <header className="wv-head">
      <div className="wv-head-main">
        <strong className="wv-title">{p.title}</strong>
        <span className={`wv-state wv-state-${state}`}>{STATE_WORD[state] ?? state}</span>
        {s.session?.branch && <span className="wv-chip">{s.session.branch}</span>}
        <span className="wv-head-actions">
          {p.actions}
          {p.onViewMode && (
            <select value={p.viewMode} onChange={(e) => p.onViewMode!(e.target.value as ViewMode)} aria-label="When the Work view opens">
              <option value="hidden">Hidden: runs silently</option>
              <option value="auto">Opens when Claude needs you</option>
              <option value="dig">Dig mode: full page</option>
            </select>
          )}
          {p.onClose && (
            <button type="button" className="wv-quiet" onClick={p.onClose} aria-label="Close the Work view">
              Close
            </button>
          )}
        </span>
      </div>
      <div className="wv-head-sub">
        {auth ? (
          <span className={auth.ok ? "wv-auth ok" : "wv-auth bad"} title={`API key source: ${auth.apiKeySource}`}>
            {auth.ok ? `${auth.subscriptionType ?? "Plan"} login${auth.email ? ` (${auth.email})` : ""}, no API key` : (auth.problem ?? "Not on the plan login")}
          </span>
        ) : (
          <span className="wv-muted">Login shows when the session starts</span>
        )}
        {s.session?.model && <span className="wv-muted">{s.session.model}</span>}
        {s.rate?.utilization !== undefined && (
          <span className={s.rate.status === "allowed" ? "wv-muted" : "wv-warn"}>
            {Math.round(s.rate.utilization * 100)}% of the {s.rate.window?.replace("_", " ") ?? "plan"} window used
          </span>
        )}
        {p.canDrive && (
          <>
            <select value={s.mode} disabled={busy} onChange={(e) => void run({ type: "mode.set", mode: e.target.value as "default" | "plan" | "acceptEdits" | "auto" })} aria-label="Permission mode">
              {(["default", "plan", "acceptEdits", "auto"] as PermissionMode[]).map((m) => (
                <option key={m} value={m}>
                  {MODE_WORD[m]}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
      {s.status.state === "waiting" && s.status.detail && <p className="wv-waiting">Claude is waiting for you: {s.status.detail}</p>}
    </header>
  );
}

function Chat(props: { s: WorkState; canDrive: boolean; onSelect: (id: string) => void; onOpen: (path: string) => void; answer: (id: string) => (a: PromptAnswer) => Promise<void>; selected: string | null; send: (c: WorkCommand) => Promise<void> }) {
  const { s } = props;
  const end = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  useEffect(() => {
    if (stick.current) end.current?.scrollIntoView({ block: "end" });
  }, [s.lastSeq, s.typing]);
  return (
    <div className="wv-chat">
      <div
        className="wv-turns"
        ref={box}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        {!s.turns.length && <p className="wv-muted wv-pad">No turns yet. Ask Claude in a room, or write below.</p>}
        {s.turns.map((t, i) => (
          <TurnBlock key={`${t.id}-${i}`} t={t} last={i === s.turns.length - 1} typing={i === s.turns.length - 1 ? s.typing : ""} {...props} />
        ))}
        <div ref={end} />
      </div>
      {props.canDrive && <Composer send={props.send} working={["working", "waiting", "compacting", "starting"].includes(s.status.state)} />}
    </div>
  );
}

function TurnBlock({ t, typing, onSelect, onOpen, answer, selected, canDrive, s }: { t: Turn; last: boolean; typing: string; s: WorkState; canDrive: boolean; onSelect: (id: string) => void; onOpen: (path: string) => void; answer: (id: string) => (a: PromptAnswer) => Promise<void>; selected: string | null }) {
  return (
    <article className="wv-turn">
      {t.id !== "start" && (
        <div className="wv-ask">
          <small>
            {t.from || "Owner"}
            {t.roomName ? ` in #${t.roomName}` : " in the Work view"} · {new Date(t.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </small>
          <p>{t.text}</p>
        </div>
      )}
      {t.items.map((it, i) => (
        <ItemRow key={i} it={it} onSelect={onSelect} onOpen={onOpen} answer={answer} selected={selected} canDrive={canDrive} cwd={s.session?.cwd ?? ""} />
      ))}
      {typing && (
        <div className="wv-text wv-typing">
          <Markdown text={typing} />
        </div>
      )}
      {t.finished && !t.finished.ok && (
        <p className="wv-error">
          Stopped ({t.finished.subtype.replace(/_/g, " ")}): {t.finished.result}
        </p>
      )}
      {t.usage && <TurnUsage u={t.usage} />}
    </article>
  );
}

function ItemRow({ it, onSelect, onOpen, answer, selected, canDrive, cwd }: { it: Item; onSelect: (id: string) => void; onOpen: (path: string) => void; answer: (id: string) => (a: PromptAnswer) => Promise<void>; selected: string | null; canDrive: boolean; cwd: string }) {
  switch (it.kind) {
    case "text":
      return (
        <div className={`wv-text ${it.parent ? "wv-sub" : ""}`}>
          <Markdown text={it.text} />
        </div>
      );
    case "tool":
      return <ToolRow item={it} onSelect={onSelect} onOpen={onOpen} selected={selected === it.id} cwd={cwd} />;
    case "prompt":
      return <PromptCard prompt={it.prompt} status={it.status} by={it.by} note={it.note} answers={it.answers} canAnswer={canDrive} onAnswer={answer(it.prompt.promptId)} />;
    case "plan":
      return (
        <div className="wv-plan-row">
          <span className="wv-chip">Plan</span> A plan is ready for approval.
        </div>
      );
    case "checklist": {
      const done = it.items.filter((t) => t.status === "completed").length;
      return (
        <div className="wv-mini-check">
          <small className="wv-muted">
            Checklist, {done} of {it.items.length} done
          </small>
          <ol className="wv-todos">
            {it.items.map((t) => (
              <li key={t.id} className={`wv-todo-${t.status}`}>
                <span className="wv-check" />
                <span>{t.status === "in_progress" && t.activeForm ? t.activeForm : t.content}</span>
              </li>
            ))}
          </ol>
        </div>
      );
    }
    case "notice":
      return <p className="wv-notice">{it.text}</p>;
    case "error":
      return (
        <p className="wv-error">
          {it.text}
          {it.hint && <span className="wv-hint"> What to do: {it.hint}</span>}
        </p>
      );
  }
}

const relative = (path: string, cwd: string) => (cwd && path.startsWith(`${cwd}/`) ? path.slice(cwd.length + 1) : path);

function ToolRow({ item, onSelect, onOpen, selected, cwd }: { item: ToolItem; onSelect: (id: string) => void; onOpen: (path: string) => void; selected: boolean; cwd: string }) {
  const [openInline, setOpenInline] = useState(false);
  const path = typeof item.input.file_path === "string" ? item.input.file_path : "";
  return (
    <div className={`wv-tool wv-tool-${item.status} ${selected ? "is-selected" : ""} ${item.parent ? "wv-sub" : ""}`}>
      <div className="wv-tool-line">
        <span className="wv-dot" aria-label={item.status === "running" ? "Running" : item.status === "error" ? "Failed" : "Done"} />
        <button type="button" className="wv-tool-title" onClick={() => onSelect(item.id)} title="Show in the live pane">
          {item.title}
        </button>
        {path && (
          <button type="button" className="wv-link" onClick={() => onOpen(path)} title={`Open ${path}`}>
            open
          </button>
        )}
        {item.output && (
          <button type="button" className="wv-link" onClick={() => setOpenInline(!openInline)} aria-expanded={openInline}>
            {openInline ? "less" : "more"}
          </button>
        )}
      </div>
      {openInline && item.output && (
        <div className="wv-tool-inline">
          <Output tool={item.tool} input={item.input} output={item.output} cwd={cwd} onOpen={onOpen} compact />
        </div>
      )}
    </div>
  );
}

function ToolDetail({ item, cwd, onOpen, following, onFollow }: { item: ToolItem; cwd: string; onOpen: (path: string) => void; following: boolean; onFollow: () => void }) {
  return (
    <div className="wv-detail">
      <div className="wv-detail-head">
        <strong>{item.title}</strong>
        <span className="wv-muted">{item.tool}</span>
        {!following && (
          <button type="button" className="wv-link" onClick={onFollow}>
            Follow the latest
          </button>
        )}
      </div>
      {item.tool === "Bash" && <pre className="wv-cmd">$ {String(item.input.command ?? "")}</pre>}
      {item.output ? <Output tool={item.tool} input={item.input} output={item.output} cwd={cwd} onOpen={onOpen} /> : <p className="wv-muted">Running…</p>}
      {item.truncated && <p className="wv-muted">The output was long; the end is cut here.</p>}
      {!["Bash", "Read", "Edit", "Write", "MultiEdit", "Grep", "Glob"].includes(item.tool) && Object.keys(item.input).length > 0 && (
        <details className="wv-card-details">
          <summary>Input</summary>
          <pre className="wv-cmd">{JSON.stringify(item.input, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

function Output({ tool, input, output, cwd, onOpen, compact }: { tool: string; input: Record<string, unknown>; output: ToolOutput; cwd: string; onOpen: (path: string) => void; compact?: boolean }) {
  const cut = (t: string) => (compact && t.length > 4000 ? `${t.slice(0, 4000)}\n…` : t);
  switch (output.kind) {
    case "read":
      return <CodeView path={output.path} text={compact ? output.content.split("\n").slice(0, 40).join("\n") : output.content} startLine={output.startLine} />;
    case "image":
      return output.data ? (
        <div className="wv-image">
          <img src={`data:${output.mime};base64,${output.data}`} alt={output.path} />
        </div>
      ) : (
        <p className="wv-muted">An image ({output.path}).</p>
      );
    case "edit":
      return <DiffView path={output.path} hunks={output.hunks} created={output.created} content={output.content} />;
    case "bash":
      return (
        <div className="wv-term">
          {output.stdout && <pre>{cut(output.stdout)}</pre>}
          {output.stderr && <pre className="wv-stderr">{cut(output.stderr)}</pre>}
          {!output.stdout && !output.stderr && <p className="wv-muted">No output.</p>}
          {output.interrupted && <p className="wv-warn">Interrupted.</p>}
          {output.background && <p className="wv-muted">Running in the background ({output.background}).</p>}
        </div>
      );
    case "search":
      return (
        <div className="wv-search">
          <p className="wv-muted">
            {output.count} {tool === "Glob" ? "files" : "results"}
            {typeof input.pattern === "string" ? ` for ${input.pattern}` : ""}
          </p>
          {output.files.length > 0 && (
            <ul>
              {output.files.slice(0, compact ? 15 : 500).map((f) => (
                <li key={f}>
                  <button type="button" className="wv-link" onClick={() => onOpen(f)}>
                    {relative(f, cwd)}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {output.text && <pre className="wv-cmd">{cut(output.text)}</pre>}
        </div>
      );
    case "error":
      return <pre className="wv-stderr">{output.text}</pre>;
    case "text":
      return (
        <div className="wv-textout">
          <pre>{cut(output.text)}</pre>
          {output.images ? <p className="wv-muted">With {output.images} {output.images === 1 ? "image" : "images"}.</p> : null}
        </div>
      );
  }
}

function Files({ s, file, onOpen }: { s: WorkState; file: { path: string; loading: boolean; error?: string; data?: OpenedFile } | null; onOpen: (p: string) => void }) {
  const cwd = s.session?.cwd ?? "";
  return (
    <div className="wv-files">
      {file && (
        <div className="wv-file">
          <div className="wv-detail-head">
            <strong>{relative(file.path, cwd)}</strong>
            {file.data && <span className="wv-muted">{Math.max(1, Math.round(file.data.size / 1024))} KB</span>}
          </div>
          {file.loading && <p className="wv-muted">Opening…</p>}
          {file.error && <p className="wv-error">{file.error}</p>}
          {file.data && <FileView {...file.data} path={file.path} />}
        </div>
      )}
      <h4>Files this session touched</h4>
      {s.files.length ? (
        <ul>
          {s.files.map((f) => (
            <li key={f}>
              <button type="button" className="wv-link" onClick={() => onOpen(f)}>
                {relative(f, cwd)}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="wv-muted">None yet.</p>
      )}
    </div>
  );
}

function PlanPane({ s }: { s: WorkState }) {
  if (!s.plan?.text) return <p className="wv-muted wv-pad">No plan in this session yet. Switch the mode to Plan first, or ask Claude to plan.</p>;
  const word = { proposed: "Waiting for approval", approved: "Approved", changes: "Sent back for changes" }[s.plan.status];
  return (
    <div className="wv-pad">
      <p>
        <span className={`wv-chip wv-plan-${s.plan.status}`}>{word}</span>
      </p>
      <Markdown text={s.plan.text} className="wv-plan-text" />
    </div>
  );
}

function Checklist({ s }: { s: WorkState }) {
  if (!s.todos.length) return <p className="wv-muted wv-pad">Claude keeps a checklist for work with several steps. It shows here, live.</p>;
  const done = s.todos.filter((t) => t.status === "completed").length;
  return (
    <div className="wv-pad">
      <p className="wv-muted">
        {done} of {s.todos.length} done
      </p>
      <ol className="wv-todos">
        {s.todos.map((t) => (
          <li key={t.id} className={`wv-todo-${t.status}`}>
            <span className="wv-check" aria-label={t.status === "completed" ? "Done" : t.status === "in_progress" ? "In progress" : "To do"} />
            <span>{t.status === "in_progress" && t.activeForm ? t.activeForm : t.content}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function TurnUsage({ u }: { u: Usage }) {
  return (
    <p className="wv-usage-line">
      in {fmt(u.input)} · out {fmt(u.output)} · cache read {fmt(u.cacheRead)}, written {fmt(u.cacheWrite)} · {money(u.costUsd)} estimate · {secs(u.durationMs)}
    </p>
  );
}

function UsagePane({ s }: { s: WorkState }) {
  const u = s.usage.session;
  const turns = s.turns.filter((t) => t.usage);
  return (
    <div className="wv-pad wv-usage">
      {u ? (
        <>
          <table>
            <tbody>
              <tr>
                <th>Input</th>
                <td>{fmt(u.input)}</td>
              </tr>
              <tr>
                <th>Output</th>
                <td>{fmt(u.output)}</td>
              </tr>
              <tr>
                <th>Cache read</th>
                <td>{fmt(u.cacheRead)}</td>
              </tr>
              <tr>
                <th>Cache written</th>
                <td>{fmt(u.cacheWrite)}</td>
              </tr>
              <tr>
                <th>Duration</th>
                <td>{secs(u.durationMs)}</td>
              </tr>
              <tr>
                <th>API value</th>
                <td>{money(u.costUsd)}</td>
              </tr>
            </tbody>
          </table>
          <p className="wv-muted">The API value is the SDK's own estimate. On your Max plan nothing is billed per token; the plan's limits apply.</p>
          {Object.keys(s.usage.models).length > 1 && (
            <>
              <h4>By model</h4>
              <ul>
                {Object.entries(s.usage.models).map(([m, x]) => (
                  <li key={m}>
                    {m}: in {fmt(x.input)}, out {fmt(x.output)}, {money(x.costUsd)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : (
        <p className="wv-muted">Usage shows after the first turn.</p>
      )}
      {s.rate && (
        <p className={s.rate.creditsRequired ? "wv-error" : "wv-muted"}>
          {s.rate.creditsRequired ? "The plan's usage is used up" : "Plan window"}
          {s.rate.utilization !== undefined ? `: ${Math.round(s.rate.utilization * 100)}% used` : ""}
          {s.rate.resetsAt ? `, resets ${new Date(s.rate.resetsAt * 1000).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}` : ""}.
        </p>
      )}
      {turns.length > 0 && (
        <>
          <h4>Per turn</h4>
          <ol className="wv-turn-usage">
            {turns.map((t, i) => (
              <li key={`${t.id}-${i}`}>
                <span>{t.text.slice(0, 60) || "Turn"}</span>
                <TurnUsage u={t.usage!} />
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

function Composer({ send, working }: { send: (c: WorkCommand) => Promise<void>; working: boolean }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [stopping, setStopping] = useState(false);
  const stop = async () => {
    setStopping(true);
    try {
      await send({ type: "interrupt" });
    } finally {
      setStopping(false);
    }
  };
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      await send({ type: "message.send", text: t });
      setText("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Not sent.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="wv-composer">
      <textarea
        value={text}
        rows={2}
        placeholder={working ? "Claude is working. Write the next step (it waits its turn), or Stop" : "Write to Claude in this session (the answer stays here)"}
        aria-label="Message to Claude"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void go();
          }
        }}
      />
      {working && (
        <button type="button" className="wv-stop" disabled={stopping} onClick={() => void stop()} title="Stop what Claude is doing now. It keeps everything done so far.">
          {stopping ? "Stopping" : "Stop"}
        </button>
      )}
      <button type="button" className="wv-primary" disabled={busy || !text.trim()} onClick={() => void go()}>
        Send
      </button>
      {error && <p className="wv-error">{error}</p>}
    </div>
  );
}
